// Якість зв'язку під час розмови: індикатор на екрані дзвінка і адаптивний бітрейт власного звуку.
// Звук, який ми чуємо, кодує АТС (Asterisk перекодовує) — на нього впливаємо лише тим, що не перевантажуємо канал.

export type CallQuality = 'good' | 'weak' | 'bad';

// накопичувальні лічильники WebRTC на момент заміру
export interface QualitySample {
  packetsReceived: number;
  packetsLost: number;
  jitterMs: number;
  // затримка туди-назад до АТС
  rttMs: number;
  // частка втрачених пакетів нашого звуку за звітом АТС (RTCP), 0..1
  remoteLoss: number;
}

export interface QualitySource {
  sample(): Promise<QualitySample | null>;
  // null — без обмеження (звичайна якість)
  setMaxBitrate(bps: number | null): Promise<void>;
}

const INTERVAL_MS = 3000;
// бітрейт нашого Opus: звичайний (без обмеження), на слабкому і поганому зв'язку
const BITRATE: Record<CallQuality, number | null> = { good: null, weak: 20000, bad: 12000 };
// покращення приймаємо, лише якщо воно трималося стільки замірів поспіль — щоб не «смикати» якість
const UPGRADE_AFTER = 2;
const RANK: Record<CallQuality, number> = { good: 0, weak: 1, bad: 2 };

export function classify(loss: number, rttMs: number, jitterMs: number): CallQuality {
  if (loss >= 0.1 || rttMs >= 800) return 'bad';
  if (loss >= 0.03 || rttMs >= 400 || jitterMs >= 60) return 'weak';
  return 'good';
}

export class CallQualityMonitor {
  private timer: ReturnType<typeof setInterval> | null = null;
  private previous: QualitySample | null = null;
  private level: CallQuality = 'good';
  private betterStreak = 0;
  // перший замір повідомляємо завжди (і «добрий») — щоб індикатор з'явився одразу
  private reported = false;

  constructor(private source: QualitySource, private onChange: (quality: CallQuality) => void) {}

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick().catch(() => {}), INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private async tick(): Promise<void> {
    const sample = await this.source.sample();
    if (!sample) return;
    const previous = this.previous;
    this.previous = sample;
    if (!previous) return;

    // втрати вхідного звуку за останній інтервал і втрати нашого звуку за звітом АТС — беремо гірше
    const received = Math.max(0, sample.packetsReceived - previous.packetsReceived);
    const lost = Math.max(0, sample.packetsLost - previous.packetsLost);
    const inboundLoss = received + lost > 0 ? lost / (received + lost) : 0;
    // за 3 с вхідних пакетів немає зовсім — канал фактично мертвий
    const stalled = received === 0;
    const loss = stalled ? 1 : Math.max(inboundLoss, sample.remoteLoss);
    const measured = classify(loss, sample.rttMs, sample.jitterMs);

    let next = this.level;
    if (RANK[measured] > RANK[this.level]) {
      next = measured;
      this.betterStreak = 0;
    } else if (RANK[measured] < RANK[this.level]) {
      this.betterStreak++;
      if (this.betterStreak >= UPGRADE_AFTER) {
        next = measured;
        this.betterStreak = 0;
      }
    } else {
      this.betterStreak = 0;
    }

    if (!this.reported) {
      this.reported = true;
      this.level = measured;
      this.onChange(measured);
      if (measured !== 'good') await this.source.setMaxBitrate(BITRATE[measured]).catch(() => {});
      return;
    }
    if (next !== this.level) {
      this.level = next;
      this.onChange(next);
      await this.source.setMaxBitrate(BITRATE[next]).catch(() => {});
    }
  }
}

// Android (WebView): статистика й обмеження бітрейту стандартного RTCPeerConnection
export function webQualitySource(peer: RTCPeerConnection): QualitySource {
  return {
    async sample() {
      const report = await peer.getStats();
      const result: QualitySample = { packetsReceived: 0, packetsLost: 0, jitterMs: 0, rttMs: 0, remoteLoss: 0 };
      report.forEach((stats: any) => {
        if (stats.type === 'inbound-rtp' && stats.kind === 'audio') {
          result.packetsReceived = stats.packetsReceived ?? 0;
          result.packetsLost = stats.packetsLost ?? 0;
          result.jitterMs = (stats.jitter ?? 0) * 1000;
        } else if (stats.type === 'remote-inbound-rtp' && stats.kind === 'audio') {
          result.remoteLoss = stats.fractionLost ?? 0;
          if (stats.roundTripTime) result.rttMs = Math.max(result.rttMs, stats.roundTripTime * 1000);
        } else if (stats.type === 'candidate-pair' && stats.nominated && stats.currentRoundTripTime) {
          result.rttMs = Math.max(result.rttMs, stats.currentRoundTripTime * 1000);
        }
      });
      return result;
    },
    async setMaxBitrate(bps) {
      for (const sender of peer.getSenders()) {
        if (sender.track?.kind !== 'audio') continue;
        const params = sender.getParameters();
        if (!params.encodings?.length) params.encodings = [{}];
        params.encodings.forEach(encoding => {
          if (bps) encoding.maxBitrate = bps;
          else delete encoding.maxBitrate;
        });
        await sender.setParameters(params);
      }
    }
  };
}
